/**
 * /native-oauth-bridge — Pagina ponte per OAuth nel Safari View modale (iOS).
 *
 * iter v29 r199 — Bridge AGGRESSIVO multi-metodo.
 *
 * Strategia (Emergent Auth supporta SOLO redirect HTTPS, non custom schemes):
 *   1. Emergent Auth fa redirect HTTPS a questa pagina con #session_id=XXX
 *   2. Tentiamo TRE metodi paralleli per aprire l'app via custom scheme:
 *      a) iframe (più affidabile in SFSafariViewController)
 *      b) <a> click programmatico (richiede activation)
 *      c) window.location.href (fallback)
 *   3. Dopo 1.5s mostriamo bottone manuale "Apri Pronto" (per utenti dove
 *      l'auto-redirect è bloccato da Safari View security policy).
 *   4. Dopo 8s, fallback HTTPS classico a /dashboard.
 *
 * Su web (browser senza app installata): naviga direttamente a /dashboard
 * preservando il fragment.
 */
import React, { useEffect, useRef, useState } from "react";

export default function NativeOAuthBridge() {
  const [showManualButton, setShowManualButton] = useState(false);
  const [debug, setDebug] = useState("");
  const iframeRef = useRef(null);
  const anchorRef = useRef(null);

  // Calcola URL custom scheme una volta sola
  const hash = typeof window !== "undefined" ? window.location.hash || "" : "";
  const search = typeof window !== "undefined" ? window.location.search || "" : "";
  const customUrl = `it.prontoimmo.app://oauth-complete${search}${hash}`;
  const hasSession = (hash + search).includes("session_id=");

  useEffect(() => {
    if (typeof window === "undefined") return;

    // Debug visibile (per capire se siamo arrivati con session_id)
    const sessionIdMatch = (hash + search).match(/session_id=([A-Za-z0-9._-]+)/);
    setDebug(sessionIdMatch ? `Token ricevuto · ${sessionIdMatch[1].slice(0, 8)}…` : "Nessun token nel link");

    if (!hasSession) {
      // Niente session_id — non c'è nulla da fare, mostra subito manuale
      const t = setTimeout(() => setShowManualButton(true), 800);
      return () => clearTimeout(t);
    }

    // Metodo 1: iframe (il PIÙ affidabile dentro SFSafariViewController)
    try {
      if (iframeRef.current) {
        iframeRef.current.src = customUrl;
      }
    } catch (_) { /* swallow */ }

    // Metodo 2: anchor click programmatico (~100ms dopo)
    const t1 = setTimeout(() => {
      try {
        anchorRef.current?.click();
      } catch (_) { /* swallow */ }
    }, 120);

    // Metodo 3: window.location.href (~300ms dopo)
    const t2 = setTimeout(() => {
      try {
        window.location.href = customUrl;
      } catch (_) { /* swallow */ }
    }, 300);

    // Mostra bottone manuale dopo 1.5s se ancora qui
    const t3 = setTimeout(() => setShowManualButton(true), 1500);

    // Ultimo fallback dopo 8s: naviga a /dashboard via HTTPS
    // (utente nel browser normale senza app)
    const t4 = setTimeout(() => {
      window.location.replace(`/dashboard${search}${hash}`);
    }, 8000);

    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
      clearTimeout(t4);
    };
  }, [hash, search, customUrl, hasSession]);

  const onManualOpen = () => {
    try {
      window.location.href = customUrl;
    } catch (_) { /* swallow */ }
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "#F1F4ED",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: "1.5rem",
        fontFamily: "system-ui, -apple-system, sans-serif",
        padding: "2rem",
        paddingTop: "max(env(safe-area-inset-top), 2rem)",
        paddingBottom: "max(env(safe-area-inset-bottom), 2rem)",
      }}
      data-testid="native-oauth-bridge"
    >
      {/* iframe invisibile per metodo 1 */}
      <iframe
        ref={iframeRef}
        title="oauth-bridge"
        style={{ display: "none" }}
        aria-hidden="true"
      />
      {/* anchor invisibile per metodo 2 */}
      <a
        ref={anchorRef}
        href={customUrl}
        style={{ display: "none" }}
        aria-hidden="true"
      >
        bridge
      </a>

      <img
        src="/logo/mark-symbol.png?v=8"
        alt="Pronto"
        style={{ width: 64, height: 64 }}
      />

      <div
        style={{
          fontSize: 14,
          color: "#7C8B6E",
          letterSpacing: "0.18em",
          textTransform: "uppercase",
          textAlign: "center",
        }}
      >
        Accesso completato
      </div>

      <div
        style={{
          fontSize: 13,
          color: "#9A968B",
          textAlign: "center",
          maxWidth: 320,
          lineHeight: 1.5,
        }}
      >
        {debug}
      </div>

      {showManualButton && (
        <button
          onClick={onManualOpen}
          data-testid="native-oauth-bridge-manual-open"
          style={{
            marginTop: "1rem",
            background: "#7C8B6E",
            color: "#FFFFFF",
            border: "none",
            borderRadius: 999,
            padding: "0.9rem 2rem",
            fontSize: 15,
            fontWeight: 600,
            cursor: "pointer",
            boxShadow: "0 4px 14px rgba(124,139,110,0.35)",
          }}
        >
          Torna nell&apos;app Pronto
        </button>
      )}

      {showManualButton && (
        <div
          style={{
            fontSize: 11,
            color: "#9A968B",
            textAlign: "center",
            maxWidth: 280,
            lineHeight: 1.4,
            marginTop: "0.5rem",
          }}
        >
          Se non succede nulla, chiudi questa finestra con la <strong>X in alto a sinistra</strong> per tornare nell&apos;app.
        </div>
      )}
    </div>
  );
}
